<script setup>
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { currencyFormatter } from 'next/message/bubbles/TiktokShop/helper/formatter.js';
import { computed, defineProps } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  original: {
    type: Object,
    required: true,
  },
  cached: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const orderNumber = computed(() => {
  return props.original.orderId || '';
});

const orderStatus = computed(() => {
  const status = props.cached.status?.downcase || '';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
});

const orderPrice = computed(() => {
  return currencyFormatter(props.cached.totalAmount);
});

const orderOrderedAt = computed(() => {
  return props.cached.createdTime;
});

const orderItemsAmount = computed(() => {
  return props.cached.items?.length;
});

const thumbnailProductImage = computed(() => {
  return props.cached?.items?.[0]?.meta?.imageUrl || null;
});

const thumbnailProductName = computed(() => {
  return props.cached?.items?.[0]?.itemName || '';
});

const thumbnailProductSKU = computed(() => {
  return props.cached?.items?.[0]?.itemSku || '';
});

const openOrderDetail = () => {
  window.open(
    `https://seller-vn.tiktok.com/order/detail?order_no=${props.original.orderId}&shop_region=VN`
  );
};
</script>

<template>
  <div
    class="tts-order max-w-md w-full cursor-pointer"
    @click="openOrderDetail"
  >
    <p class="flex items-center gap-2 border-b border-slate-100 pb-2">
      <Icon icon="i-ri-todo-fill" />
      <span class="font-semibold text-base">
        {{ t('CONVERSATION.TIKTOKSHOP.ORDER.LABEL') }}
      </span>
    </p>
    <div class="mb-2">
      <span class="font-semibold">{{ orderStatus }}</span>
      <p class="text-xs mt-1">
        <span class="me-4">{{
          t('CONVERSATION.TIKTOKSHOP.ORDER.ORDER_ID')
        }}</span>
        <span>{{ orderNumber }}</span>
      </p>
    </div>
    <div class="flex gap-2 bg-slate-100/30 p-4 rounded-lg items-start">
      <img
        :src="thumbnailProductImage"
        alt="Order thumbnail image"
        class="w-16 h-16 object-cover rounded-lg"
      />
      <div class="flex flex-col text-xs gap-1">
        <span class="font-medium text-gray-800">{{
          thumbnailProductName
        }}</span>
        <span class="text-gray-500">{{ thumbnailProductSKU }}</span>
      </div>
    </div>
    <p class="flex justify-between mt-2">
      <span class="font-semibold">{{
        t('CONVERSATION.TIKTOKSHOP.ORDER.ORDERED_AT')
      }}</span>
      <span>{{ orderOrderedAt }}</span>
    </p>
    <p class="flex justify-between">
      <span class="font-semibold">{{
        t('CONVERSATION.TIKTOKSHOP.ORDER.ITEMS_AMOUNT')
      }}</span>
      <span>{{ orderItemsAmount }}</span>
    </p>
    <p class="flex justify-between">
      <span class="font-semibold">{{
        t('CONVERSATION.TIKTOKSHOP.ORDER.TOTAL_PRICE')
      }}</span>
      <span class="font-semibold text-woot-600">{{ orderPrice }}</span>
    </p>
  </div>
</template>

<style lang="scss" scoped>
.tts-order {
  @apply bg-white dark:bg-n-slate-4 p-4 rounded-lg shadow-lg mb-4 flex flex-col;
}
</style>
